import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { twm } from '../../../core/themes/tw-merge';
import { EyeIcon, EyeSlashIcon } from '../../../icons';
import { InfoPopover } from '../InfoPopover';
import { Label } from '../typography';

export type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  errorMessage?: string;
  icon?: ReactNode;
  onIconClick?: () => void;
  children?: ReactNode;
  info?: string;
  rightActionElement?: ReactNode;
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    { className, label, id, errorMessage, icon, onIconClick, children, type, info, rightActionElement, ...rest },
    ref,
  ) => {
    const [secure, setSecure] = useState(true);

    const action =
      rightActionElement ||
      (type === 'password' ? (
        <button
          type="button"
          className={twJoin('absolute right-0 top-0 h-12 flex items-center justify-center w-10 text-text-tertiary')}
          onClick={() => setSecure(prev => !prev)}
        >
          {secure ? <EyeIcon /> : <EyeSlashIcon />}
        </button>
      ) : null);

    return (
      <div>
        {label ? (
          <div className="flex items-center gap-2 mb-1.5">
            <Label htmlFor={id} className={twJoin(!!errorMessage && 'text-text-error', 'mb-0')}>
              {label}
            </Label>
            {info ? <InfoPopover text={info} /> : null}
          </div>
        ) : null}
        <div className="relative">
          {icon ? (
            <button
              type="button"
              className={twm(
                'absolute left-0 top-0 h-12 flex items-center justify-center w-10 text-text-tertiary',
                !onIconClick && 'cursor-default',
                !!errorMessage && 'text-text-error',
              )}
              onClick={onIconClick}
            >
              {icon}
            </button>
          ) : null}
          <input
            {...rest}
            ref={ref}
            id={id}
            type={type === 'password' ? (secure ? 'password' : 'text') : type}
            className={twm(
              'bg-surface-secondary w-full rounded-md h-12 px-3 placeholder:text-text-disabled outline-none focus:ring-surface-accent focus:ring-2',
              !!errorMessage &&
                'placeholder-fg-error-primary border-2 border-border-error text-text-error outline-none focus:ring-0',
              !!icon && 'pl-10',
              !!action && 'pr-10',
              className,
            )}
          />
          {action}
          {children}
        </div>
        {errorMessage ? <p className="mt-1 text-text-error text-body-sm leading-lh-body-sm">{errorMessage}</p> : null}
      </div>
    );
  },
);

TextInput.displayName = 'TextInput';
